/**
 * AnimationSequence property-binding shortcuts — shared editor utilities.
 *
 * The animation analog of `renderer-bindings.ts`. They wire a state-swappable
 * animation clip the same way the material shortcut wires a state-swappable
 * material: bind an `AnimationSequence` slot's `src` to a `kind: 'animation-clip'`
 * Properties declaration, so a state can override which clip plays (enter /
 * while-active / exit) by overriding one leaf-string property value.
 *
 * - `bindAnimationClipToProperty()` — full shortcut: ensure an AnimationSequence
 *   and a Properties behavior, add a `kind: 'animation-clip'` declaration seeded
 *   from the slot's current `src`, and bind `states[slot].src` to it. One undo
 *   entry.
 * - `applyClipSrc()` — set a slot's clip src WITHOUT destroying a binding. Routes
 *   to the **active state** override when one is active (so picking a clip while
 *   previewing a state authors that state), to the declaration's base value at
 *   Base, or directly to the AnimationSequence when the slot is unbound.
 *
 * Taking a binding down when its last state is deleted is
 * `animation-binding-teardown.ts`, folded into the delete's undo entry.
 *
 * All take SceneObject refs (never name strings) and record undo via the shared
 * UndoManager unless `record: false`, in which case the built command is returned
 * for composition into a caller-owned CompositeCommand.
 */

import type { SceneObject } from '../core/SceneObject';
import type { ValidationError } from '../scenedata/behaviordata';
import type {
    AnimationSequenceData,
    AnimationSequenceStates,
    AnimationStateConfig,
} from '../scenedata/behaviors/animationsequencedata';
import type { PropertyDeclaration, StringDeclaration } from '../scenedata/behaviors/propertiesdata';
import { isPropertyBinding, wholeRefName, WHOLE_REF_RE } from '../behaviors/tracebehavior';
import { AnimationSequence } from '../behaviors/animationsequence';
import { Properties } from '../behaviors/properties';
import type { UndoCommand } from './undo/UndoCommand';
import { getUndoManager } from './undo/UndoManager';
import { BehaviorCommitCommand } from './undo/BehaviorCommitCommand';
import { AddBehaviorCommand } from './undo/AddBehaviorCommand';
import { CompositeCommand } from './undo/CompositeCommand';
import { dispatchBehaviorChanged } from './undo/editor-events';
import { editTargetState, captureStateValue, stateEditRefused } from './states/statesActions';

/** Which AnimationSequence state (clip slot) a binding/edit targets. */
export type ClipSlot = 'enter' | 'main' | 'exit';

export interface BindCommandOptions {
    /**
     * Record the operation on the shared UndoManager (default true).
     * Pass false to compose the returned command into a CompositeCommand.
     */
    record?: boolean;
}

/** Default property name for each slot's bound clip src. */
const DEFAULT_PROP_FOR_SLOT: Record<ClipSlot, string> = {
    enter: 'animEnter',
    main: 'animMain',
    exit: 'animExit',
};

/** Read a slot's authored config (may hold a `$ref` binding string). */
function authoredSlot(seq: AnimationSequence, slot: ClipSlot): AnimationStateConfig | undefined {
    return seq.getData().states?.[slot];
}

/** Write a single slot's config back, preserving the other slots. */
function withSlot(
    states: AnimationSequenceStates | undefined,
    slot: ClipSlot,
    config: AnimationStateConfig,
): AnimationSequenceStates {
    return { ...(states ?? {}), [slot]: config };
}

export interface BindAnimationClipOptions extends BindCommandOptions {
    /** Slot to bind. @default 'main' */
    slot?: ClipSlot;
    /** Property name to declare. De-duplicated against existing declarations. */
    propertyName?: string;
    /** Display label for the declaration (editor UI / AutoUI). */
    label?: string;
}

export interface BindAnimationClipResult {
    /** Empty on success. */
    errors: ValidationError[];
    /** The declared property name actually used (after de-duplication). */
    propertyName: string | null;
    /** Composite undo command covering all steps (null when the bind failed). */
    command: UndoCommand | null;
}

/**
 * Full "state-swappable clip" shortcut:
 *
 * 1. Ensure the object has an AnimationSequence behavior (added if missing).
 * 2. Ensure the object has a Properties behavior (added if missing).
 * 3. Add a `{ type: 'string', kind: 'animation-clip' }` declaration, seeded with
 *    the slot's current clip `src` (empty = no clip = silent at Base).
 * 4. Bind `states[slot].src` to the new property.
 *
 * All steps land as ONE undo entry. States on the Properties behavior can then
 * override the property per state to swap the clip at runtime; the
 * AnimationSequence reacts to the resolved-src change and plays it (see
 * AnimationSequence.applyCommit auto-play in play mode).
 */
export async function bindAnimationClipToProperty(
    sceneObject: SceneObject,
    options: BindAnimationClipOptions = {},
): Promise<BindAnimationClipResult> {
    const slot = options.slot ?? 'main';
    const commands: UndoCommand[] = [];
    const rollback = async (): Promise<void> => {
        for (let i = commands.length - 1; i >= 0; i--) await commands[i].undo();
    };

    // 1. Ensure AnimationSequence behavior
    let seq = sceneObject.getBehavior(AnimationSequence);
    if (!seq) {
        seq = await sceneObject.addBehavior(AnimationSequence, { states: {} } as never);
        commands.push(new AddBehaviorCommand(sceneObject, 'AnimationSequence', seq.getData()));
    }

    // 2. Ensure Properties behavior
    let properties = sceneObject.getBehavior(Properties);
    let propertiesAdded = false;
    if (!properties) {
        properties = await sceneObject.addBehavior(Properties);
        propertiesAdded = true;
        commands.push(new AddBehaviorCommand(sceneObject, 'Properties', properties.getData()));
    }

    // 3. Declare the clip property, seeded from the slot's current src
    const propsBefore = properties.getData();
    const existingNames = new Set((propsBefore.properties ?? []).map(d => d.name));
    let name = options.propertyName ?? DEFAULT_PROP_FOR_SLOT[slot];
    if (existingNames.has(name)) {
        let i = 2;
        while (existingNames.has(`${name}${i}`)) i++;
        name = `${name}${i}`;
    }

    const slotSrc = authoredSlot(seq, slot)?.src;
    const seed = slotSrc && !isPropertyBinding(slotSrc) ? slotSrc : '';

    const declaration: StringDeclaration = {
        name,
        type: 'string',
        kind: 'animation-clip',
        value: seed,
        ...(options.label ? { label: options.label } : {}),
    };
    const declarations: PropertyDeclaration[] = [...(propsBefore.properties ?? []), declaration];
    const declErrors = await properties.commit({ properties: declarations });
    if (declErrors.length > 0) {
        await rollback();
        return { errors: declErrors, propertyName: null, command: null };
    }
    commands.push(new BehaviorCommitCommand(
        sceneObject, 'Properties', propsBefore, properties.getData(), null,
    ));

    // 4. Bind states[slot].src to the property
    const seqBefore = seq.getData();
    const existingConfig = authoredSlot(seq, slot) ?? {};
    const boundStates = withSlot(seqBefore.states, slot, { ...existingConfig, src: `$${name}` });
    const bindErrors = await seq.commit({ states: boundStates });
    if (bindErrors.length > 0) {
        await rollback();
        return { errors: bindErrors, propertyName: null, command: null };
    }
    commands.push(new BehaviorCommitCommand(
        sceneObject, 'AnimationSequence', seqBefore, seq.getData(), null,
    ));

    const command = new CompositeCommand(`Bind ${slot} clip to property on ${sceneObject.name}`, commands);
    if (options.record !== false && !getUndoManager().isUndoRedoing) {
        getUndoManager().push(command);
    }
    // Announce both behaviors so each keyed UI region refreshes once.
    dispatchBehaviorChanged({
        objectPath: sceneObject.getPath(),
        behaviorType: 'Properties',
        action: propertiesAdded ? 'added' : 'updated',
    });
    dispatchBehaviorChanged({
        objectPath: sceneObject.getPath(),
        behaviorType: 'AnimationSequence',
        action: 'updated',
    });
    return { errors: [], propertyName: name, command };
}

export interface ApplyClipSrcResult {
    /** Empty on success. */
    errors: ValidationError[];
    /**
     * Where the write landed: an active-state override, the bound declaration's
     * base value, or the AnimationSequence's authored states (slot unbound).
     */
    target: 'state' | 'property' | 'sequence' | null;
    /** The bound property name, when target is 'state' or 'property'. */
    propertyName: string | null;
    /** Undo command for the write (null on failure or when handled via the shared stack). */
    command: UndoCommand | null;
}

/**
 * Set a clip slot's src WITHOUT destroying a property binding.
 *
 * State-aware: when the slot is bound and a non-Base state is active, the pick
 * is routed into that **active state's override** (authoring "this state plays
 * this clip"). At Base it updates the declaration's base value. An unbound slot
 * writes the AnimationSequence directly.
 *
 * No longer a mirror of `applyMaterialSrc`: that path now declares on the
 * EDITED OBJECT when a style supplied the value from a sheet, while this one
 * still resolves its write target through the ancestor walk and dead-ends the
 * way the material path used to. Aligning them is issue #712.
 *
 * Pass `src: ''` on a bound slot to mean "no clip" (silent) for that state/base.
 */
export async function applyClipSrc(
    sceneObject: SceneObject,
    slot: ClipSlot,
    src: string,
    options: BindCommandOptions = {},
): Promise<ApplyClipSrcResult> {
    const seq = sceneObject.getBehavior(AnimationSequence);
    if (!seq) {
        return {
            errors: [{ message: `No AnimationSequence behavior on "${sceneObject.name}"`, code: 'NO_ANIMATION_SEQUENCE' }],
            target: null, propertyName: null, command: null,
        };
    }

    const record = options.record !== false;
    const authoredSrc = authoredSlot(seq, slot)?.src;
    const refMatch = authoredSrc?.match(WHOLE_REF_RE);

    // ── Unbound slot: write the AnimationSequence directly ──
    if (!refMatch) {
        const before = seq.getData();
        const existingConfig = authoredSlot(seq, slot) ?? {};
        const states = withSlot(before.states, slot, { ...existingConfig, src });
        const errors = await seq.commit({ states });
        if (errors.length > 0) return { errors, target: null, propertyName: null, command: null };

        const command = new BehaviorCommitCommand(
            sceneObject, 'AnimationSequence', before, seq.getData(), `states.${slot}.src`,
        );
        if (record && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
        dispatchBehaviorChanged({
            objectPath: sceneObject.getPath(), behaviorType: 'AnimationSequence', action: 'updated',
        });
        return { errors: [], target: 'sequence', propertyName: null, command };
    }

    // ── Bound slot: route into the active state, else the declaration base ──
    const name = wholeRefName(refMatch);

    // If a non-Base state is active on this object's group, author the override
    // there. A refused capture (Play, no host guard) writes nothing rather than
    // Base (mirrors applyMaterialSrc).
    if (editTargetState(sceneObject) !== undefined) {
        const { captured, command } = await captureStateValue(sceneObject, name, src, { record });
        return { errors: [], target: captured ? 'state' : null, propertyName: name, command };
    }

    // Base: write the providing declaration's base value.
    let owner: SceneObject | null = sceneObject;
    let properties: Properties | null = null;
    while (owner) {
        const p = owner.getBehavior(Properties);
        if (p?.hasProperty(name)) { properties = p; break; }
        owner = owner.getParent();
    }
    if (!owner || !properties) {
        return {
            errors: [{ message: `Bound property "${name}" has no providing Properties behavior`, code: 'NO_PROVIDER' }],
            target: null, propertyName: name, command: null,
        };
    }

    const before = properties.getData();
    const declToUpdate = (before.properties ?? []).find(d => d.name === name);
    if (!declToUpdate || declToUpdate.type !== 'string') {
        return {
            errors: [{ message: `Property "${name}" is not an inline string declaration`, code: 'NOT_AUTHORABLE' }],
            target: null, propertyName: name, command: null,
        };
    }
    const declarations: PropertyDeclaration[] = (before.properties ?? []).map(d =>
        d.name === name && d.type === 'string' ? { ...d, value: src } : d,
    );
    const errors = await properties.commit({ properties: declarations });
    if (errors.length > 0) return { errors, target: null, propertyName: name, command: null };

    const command = new BehaviorCommitCommand(
        owner, 'Properties', before, properties.getData(), `property:${name}`,
    );
    if (record && !getUndoManager().isUndoRedoing) getUndoManager().push(command);
    dispatchBehaviorChanged({
        objectPath: owner.getPath(), behaviorType: 'Properties', action: 'updated',
    });
    return { errors: [], target: 'property', propertyName: name, command };
}

export interface SetClipForStateResult extends ApplyClipSrcResult {
    /** True when this call auto-promoted the slot to a state-swappable binding. */
    autoBound: boolean;
}

/**
 * Transparent "set this slot's clip for the current state context" — the
 * high-level entry point for editors (notably TraceFrontend) where the binding
 * mechanism is invisible to the user.
 *
 * The user just picks a clip while a state is active; this decides the plumbing:
 *
 * - **Non-Base state active, slot not yet bound** → auto-bind the slot to a
 *   `kind:'animation-clip'` property (one undo entry), then write the pick into
 *   that active state's override. The user never sees `$ref` or a "bind" button.
 * - **Slot already bound** → route into the active state / declaration base
 *   (delegates to {@link applyClipSrc}).
 * - **Base active (no state)** → write the slot directly (an unbound clip is the
 *   right model for "this object's only clip"); no binding is forced.
 *
 * When auto-binding, the bind + the per-state write are bundled into ONE undo
 * entry so an undo cleanly removes both.
 *
 * Pass `src: ''` to clear the clip for the current context.
 */
export async function setClipForState(
    sceneObject: SceneObject,
    slot: ClipSlot,
    src: string,
    options: BindCommandOptions = {},
): Promise<SetClipForStateResult> {
    const seq = sceneObject.getBehavior(AnimationSequence);
    const authoredSrc = seq ? authoredSlot(seq, slot)?.src : undefined;
    const alreadyBound = typeof authoredSrc === 'string' && WHOLE_REF_RE.test(authoredSrc);
    const stateActive = editTargetState(sceneObject) !== undefined;

    // Auto-bind only when it changes the outcome: a non-Base state is active and
    // the slot isn't already bound. At Base, an unbound write is correct, so we
    // skip binding to avoid littering objects with properties they don't need.
    if (stateActive && !alreadyBound) {
        // The bind is part of the state edit, so it is refused with it. Checked
        // at call time, before the bind; the capture checks again at its turn.
        if (stateEditRefused(sceneObject)) {
            return { errors: [], target: null, propertyName: null, command: null, autoBound: false };
        }
        const commands: UndoCommand[] = [];
        const bind = await bindAnimationClipToProperty(sceneObject, { slot, record: false });
        if (bind.errors.length > 0 || !bind.command) {
            return { ...bind, target: null, autoBound: false };
        }
        commands.push(bind.command);

        // Write the pick into the active state's override (bundled, not recorded).
        const apply = await applyClipSrc(sceneObject, slot, src, { record: false });
        if (apply.errors.length > 0) {
            await bind.command.undo();
            return { ...apply, autoBound: false };
        }
        if (apply.command) commands.push(apply.command);

        const command = new CompositeCommand(
            `Set ${slot} clip for state on ${sceneObject.name}`, commands,
        );
        if (options.record !== false && !getUndoManager().isUndoRedoing) {
            getUndoManager().push(command);
        }
        return { ...apply, command, autoBound: true };
    }

    // Already bound, or Base — applyClipSrc handles both (state/property/sequence).
    const result = await applyClipSrc(sceneObject, slot, src, options);
    return { ...result, autoBound: false };
}

// Re-export the data type for consumers building clip pickers.
export type { AnimationSequenceData };
